import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'antd/dist/reset.css';
import { initVersionCheck } from './services/versionCheck';

// Auth Components
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';

// Layout Components
import MainLayout from './layouts/MainLayout';

// Job Management
import JobList from './pages/jobs/JobList';
import JobCreate from './pages/jobs/JobCreate';
import JobEdit from './pages/jobs/JobEdit';
import JobDetail from './pages/jobs/JobDetail';

// Driver Management
import DriverList from './pages/drivers/DriverList';
import DriverCreate from './pages/drivers/DriverCreate';
import DriverEdit from './pages/drivers/DriverEdit';
import DriverDetail from './pages/drivers/DriverDetail';

// Van Management
import VanList from './pages/vans/VanList';
import VanCreate from './pages/vans/VanCreate';
import VanEdit from './pages/vans/VanEdit';
import VanDetail from './pages/vans/VanDetail';

// Payment Management
import PaymentList from './pages/payments/PaymentList';
import PaymentDetail from './pages/payments/PaymentDetail';
import PaymentCreate from './pages/payments/PaymentCreate';
import PaymentEdit from './pages/payments/PaymentEdit';
import OverduePayments from './pages/payments/OverduePayments';

// Reports
import ReportDashboard from './pages/reports/ReportDashboard';
import FinancialReport from './pages/reports/FinancialReport';
import RevenueReport from './pages/reports/RevenueReport';
import DriverReports from './pages/reports/DriverReports';
import VanReport from './pages/reports/VanReport';
import ResourceUtilization from './pages/reports/ResourceUtilization';

// Utility Pages
import ServerStatus from './pages/ServerStatus';

// Utils
import PrivateRoute from './components/PrivateRoute';
import { AuthProvider } from './context/AuthContext';
import { Navigate } from 'react-router-dom';
import ChannelList from './pages/channels/ChannelList';

// Invoice Management
import InvoiceList from './pages/invoices/InvoiceList';
import InvoiceCreate from './pages/invoices/InvoiceCreate';
import InvoiceView from './pages/invoices/InvoiceView';

// Reconciliation
import Reconciliation from './pages/reconciliation/Reconciliation';

// Agent
import AgentLogs from './pages/agent/AgentLogs';

// Enquiries
import EnquiryList from './pages/enquiries/EnquiryList';


const App = () => {
  useEffect(() => {
    initVersionCheck();
  }, []);

  return (
    <AuthProvider>
      <Router>
        <ToastContainer position="top-right" autoClose={3000} />
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/server-status" element={<ServerStatus />} />
          
          {/* Protected Routes */}
          <Route element={<PrivateRoute />}>
            <Route element={<MainLayout />}>
              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/jobs" replace />} />

              {/* Jobs */}
              <Route path="/jobs" element={<JobList />} />
              <Route path="/jobs/create" element={<JobCreate />} />
              <Route path="/jobs/edit/:id" element={<JobEdit />} />
              <Route path="/jobs/:id" element={<JobDetail />} />
              
              {/* Drivers */}
              <Route path="/drivers" element={<DriverList />} />
              <Route path="/drivers/create" element={<DriverCreate />} />
              <Route path="/drivers/edit/:id" element={<DriverEdit />} />
              <Route path="/drivers/:id" element={<DriverDetail />} />
              
              {/* Vans */}
              <Route path="/vans" element={<VanList />} />
              <Route path="/vans/create" element={<VanCreate />} />
              <Route path="/vans/edit/:id" element={<VanEdit />} />
              <Route path="/vans/:id" element={<VanDetail />} />
              
              {/* Payments */}
              <Route path="/payments" element={<PaymentList />} />
              <Route path="/payments/overdue" element={<OverduePayments />} />
              <Route path="/payments/create" element={<PaymentCreate />} />
              <Route path="/payments/edit/:id" element={<PaymentEdit />} />
              <Route path="/payments/:id" element={<PaymentDetail />} />
              
              {/* Channels */}
              <Route path="/channels" element={<ChannelList />} />
              
              {/* Invoices */}
              <Route path="/invoices" element={<InvoiceList />} />
              <Route path="/invoices/create" element={<InvoiceCreate />} />
              <Route path="/invoices/:id" element={<InvoiceView />} />
              
              {/* Reconciliation */}
              <Route path="/reconciliation" element={<Reconciliation />} />
              
              {/* Reports */}
              <Route path="/reports" element={<ReportDashboard />} />
              <Route path="/reports/financial" element={<FinancialReport />} />
              <Route path="/reports/revenue" element={<RevenueReport />} />
              <Route path="/reports/driver" element={<DriverReports />} />
              <Route path="/reports/van" element={<VanReport />} />
              <Route path="/reports/resources" element={<ResourceUtilization />} />

              <Route path="/agent-logs" element={<AgentLogs />} />
              <Route path="/enquiries" element={<EnquiryList />} />
            </Route>
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;